import AHref from "../../../../../components/ahref/ahref";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import ControlGroup from "../../../../../components/layouts/control-group/control-group";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import config from "../../../../../config";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { ConfirmModal } from "../../../../../components/modal/components/confirm-modal/confirm-modal";
import React, { useState } from "react";
import { rootContainer, TYPE } from "../../../../../services/container/root";
import { IIframeService } from "../../../../../services/iframe-service/iframe-service";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import {
	AUTO_PROCESS_TRIGGER,
	AUTO_PROCESS_VALIDATED_QUALIFIER,
	getCaptureTriggerTypesOptions,
	getValidatedQualifierOptions
} from "../../../../../models/User";
import { SingleValue } from "react-select";
import { observer } from "mobx-react";
import AuthPeriodInDaysField from "./auth-period-in-days-field/auth-period-in-days-field";
import { FEATURES } from "../../../../../models/Plan";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import { Input } from "../../../../../components/inputs/input/input";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";


interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    dataName?: string; /** used by Candu to target an element */
}

const PortalCheckoutCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

    const buildPaymentIntegrationNote = (rawText: string) => {
        if(typeof rawText !== 'string' || rawText.split('|').length !== 5){
            return <></>
        }
        const [ toAccess, toAccessLink, readMore, readMoreLink, post ] = rawText.split('|');
        return (<>
            {toAccess}
            <AHref
                href={config.apiUrl + '/dashboard/settings/integrations/products-services'}
                target="_parent"
            >
                {toAccessLink}
            </AHref>
            {readMore}
            <AHref
                href={'https://support.returngo.ai/instant-exchange'}
                target="_blank"
            >
                {readMoreLink}
            </AHref>
            {post}
        </>);
    };

    const instantExchangeSetting = currentPortalStore.setting?.instantExchangeStore;
    const storeSetting = currentPortalStore.setting;
    const hasPaymentServiceIntegration = instantExchangeSetting?.hasPaymentServiceIntegration;
    const hasVariantExchangeResolutionNamedInstantExchange = instantExchangeSetting?.hasVariantExchangeResolutionNamedInstantExchange;
    const paymentIntegrationNote = buildPaymentIntegrationNote(getTranslation('PAYMENT_INTEGRATION_MISSING_NOTE', pageScope));
    const isPreAuthFeatureAllowed = currentPortalStore.isFeatureAllowed(FEATURES.BETA_PRE_AUTHORIZATION) && hasPaymentServiceIntegration;
    const [isShowInstantExchangeModal, setIsShowInstantExchangeModal] = useState(false);
    const onCloseConfirm = async () => {
        setIsShowInstantExchangeModal(false);
    };
    const onRedirectConfirm = async () => {
        setIsShowInstantExchangeModal(false);
        let iframeService = rootContainer.get<IIframeService>(TYPE.IframService);
        await iframeService.redirectParentFrame(config.apiUrl + `/dashboard/settings/resolutions/`);
    };

    const onChangeInstantExchangeSetting = async (v: boolean) => {
        setIsShowInstantExchangeModal(v && hasVariantExchangeResolutionNamedInstantExchange === true);
        instantExchangeSetting?.setIsActive(v);
    };

    const _getDashTranslation = (key: string): string => {
        return getTranslation(key, pageScope);
    };

    const capturePaymentTriggerOptions = getCaptureTriggerTypesOptions(_getDashTranslation);
    const capturePaymentTriggerValue = capturePaymentTriggerOptions.find(op => op.value === currentPortalStore.setting?.capturePaymentTrigger);
    const onCapturePaymentTriggerChange = (value: SingleValue<IOption>) => {
        currentPortalStore.setting?.setCapturePaymentTrigger(value?.value as AUTO_PROCESS_TRIGGER);
    };

    const validatedQualifierOptions = getValidatedQualifierOptions(_getDashTranslation);
    const validatedQualifierValue = validatedQualifierOptions.find(op => op.value === currentPortalStore.setting?.capturePaymentValidatedQualifier);
    const onValidatedQualifierChange = (value: SingleValue<IOption>) => {
        currentPortalStore.setting?.setCapturePaymentValidatedQualifier(value?.value as AUTO_PROCESS_VALIDATED_QUALIFIER);
    };

    const requireInstantExchange = currentPortalStore.setting?.requireInstantExchange;
    const onRequireInstantExchangeChange = (value: boolean) => {
        currentPortalStore.setting?.setRequireInstantExchange(value as boolean);
    };

    const onChangePortalCheckoutSetting = async (v: boolean) => {
        storeSetting?.setEnablePortalCheckout(v);
    };


  const portalCheckoutCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PORTAL_CHECKOUT);
	const instantExchangeCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.INSTANT_EXCHANGE);
	const isInstantExchangeAllowed = instantExchangeCapability.isFeatureEnabled && hasPaymentServiceIntegration;

	if (
		(!instantExchangeCapability.isSupported || !instantExchangeCapability.isFeatureEnabled) &&
		(!portalCheckoutCapability.isSupported || !portalCheckoutCapability.isFeatureEnabled)
	) {
		return null;
	}

    return <Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="IN_PORTAL_PAYMENTS" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        {!hasPaymentServiceIntegration &&
            (
                <Note
                    body={paymentIntegrationNote}
                    type={NOTE_TYPE.INFORMATION}
                />
            )
        }

        {portalCheckoutCapability.isSupported && portalCheckoutCapability.isFeatureEnabled && (
			<>
				<FormFieldLine
					label={getTranslation('MINIMUM_PAYMENT_THRESHOLD_LABEL', pageScope)}
					note={getTranslation('MINIMUM_PAYMENT_THRESHOLD_DESCRIPTION', pageScope)}
					dataName="minimum-payment-threshold"
				>
					<Input
						adornment={storeSetting?.currencyCode}
						value={storeSetting?.minimumPaymentThreshold}
						onChange={storeSetting?.setMinimumPaymentThreshold}
						type={'number'}
						min={0.5}
						required
					/>
				</FormFieldLine>
				<ControlGroup>
					<Note
						type={NOTE_TYPE.INFORMATION}
						body={<DashTranslation translationKey="MINIMUM_PAYMENT_THRESHOLD_NOTE" scope={pageScope} />}
					/>
				</ControlGroup>
				<FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
					{getTranslation('PORTAL_CHECKOUT', pageScope)}
				</FontBody2>
	            <FormFieldLine
	                label={getTranslation('ENABLE_PORTAL_CHECKOUT', pageScope)}
	                note={getTranslation('ENABLE_PORTAL_CHECKOUT_DESCRIPTION', pageScope)}
	                dataName="enable-portal-checkout"
	            >
	                <ControlGroup>
	                    <Toggle
	                        checked={storeSetting?.enablePortalCheckout}
	                        onChange={(v) => onChangePortalCheckoutSetting(v)}
	                        disabled={!hasPaymentServiceIntegration}
	                        label=''
	                    />
	                </ControlGroup>
	            </FormFieldLine>
			</>
        )}

        {instantExchangeCapability.isSupported && instantExchangeCapability.isFeatureEnabled && (
            <>
                <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
                    {getTranslation('INSTANT_EXCHANGE', pageScope)}
                </FontBody2>
                <FormFieldLine
                    label={getTranslation('ENABLE_INSTANT_EXCHANGE', pageScope)}
                    note={getTranslation('ENABLE_INSTANT_EXCHANGE_DESCRIPTION', pageScope)}
                    disabled={!isInstantExchangeAllowed}
                    dataName="enable-instant-exchange">
                    <ControlGroup>
                        <Toggle
                            checked={instantExchangeSetting?.isActive && isInstantExchangeAllowed}
                            onChange={(v) => onChangeInstantExchangeSetting(v)}
                            disabled={!isInstantExchangeAllowed}
                            label=''

                        />
                    </ControlGroup>

                </FormFieldLine>

                {isPreAuthFeatureAllowed &&
                  <FormFieldLine
                    label={getTranslation('USE_PRE_AUTHORIZE', pageScope)}
                    note={getTranslation('USE_PRE_AUTHORIZE_DESCRIPTION', pageScope)}
                    disabled={!instantExchangeSetting?.isActive}
                    dataName="use-pre-authorize"
                  >
                      <ControlGroup>
                          <Toggle
                            checked={instantExchangeSetting?.isActive && storeSetting?.usePreAuthorize}
                            onChange={storeSetting?.setUsePreAuthorize}
                            label=''
                            disabled={!instantExchangeSetting?.isActive}
                          />
                      </ControlGroup>
                  </FormFieldLine>
                }

                <FormFieldLine
                    label={getTranslation('AUTOMATIC_PAYMENT_CAPTURE_TIMEFRAME', pageScope)}
                    note={getTranslation('AUTOMATIC_PAYMENT_CAPTURE_TIMEFRAME_DESCRIPTION', pageScope)}
                    disabled={!isInstantExchangeAllowed}
                    dataName="automatic-payment-capture-timeframe"
                >
                    <AuthPeriodInDaysField disabled={!isInstantExchangeAllowed}
                                           instantExchangeSetting={instantExchangeSetting} pageScope={pageScope}/>
                </FormFieldLine>
                <FormFieldLine
                    label={getTranslation('CAPTURE_PAYMENT_TRIGGER', pageScope)}
                    note={getTranslation('CAPTURE_PAYMENT_TRIGGER_DESCRIPTION', pageScope)}
                    disabled={!isInstantExchangeAllowed}
                    dataName="capture-payment-trigger"
                >
                    <ListLayout>
                        <SelectDropdown
                            options={capturePaymentTriggerOptions}
                            value={capturePaymentTriggerValue}
                            onChange={onCapturePaymentTriggerChange}
                            disabled={!isInstantExchangeAllowed}
                        />

                        <Note body={getTranslation('THIS_SETTING_DETERMINES_DEFAULT_CAPTURE_TRIGGER', pageScope)} />
                    </ListLayout>
                </FormFieldLine>
                {currentPortalStore.setting?.capturePaymentTrigger === AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED &&
                    <FormFieldLine 
                        label={getTranslation('VALIDATED_IS', pageScope)}
                        disabled={!isInstantExchangeAllowed}
                        dataName="validated-is">
                        <SelectDropdown
                            value={validatedQualifierValue}
                            options={validatedQualifierOptions}
                            onChange={onValidatedQualifierChange}
                            disabled={!isInstantExchangeAllowed}
                        />
                    </FormFieldLine>
                }
                {<FormFieldLine
                    label={getTranslation('REQUIRE_INSTANT_EXCHANGE', pageScope)}
                    dataName="require-instant-exchange"
                >
                    <ControlGroup>
                        <Toggle
                            checked={requireInstantExchange}
                            onChange={(v) => onRequireInstantExchangeChange(v)}
                            label=''
                        />
                    </ControlGroup>
                </FormFieldLine>}
                {isShowInstantExchangeModal &&
                    <ConfirmModal
                        title={getTranslation('WARNING', pageScope)}
                        text={getTranslation('ACCOUNT_HAS_INSTANT_EXCHANGE_RESOLUTION', pageScope)}
                        cancelButton={{
                            title: getTranslation('CLOSE', pageScope),
                            onClick: onCloseConfirm
                        }}
                        okButton={{
                            title: getTranslation('GO_TO_RESOLUTION_SETTINGS', pageScope),
                            onClick: onRedirectConfirm,
                        }}
                    />
                }
            </>
        )}
    </Card>
})

export default PortalCheckoutCard;
